import { fhr } from './utils/fhr.js';
import { config } from 'config';
import { dataSeries } from './dataSeries.js';
import { legendProportional } from './gui/legendProportional.js';
import { svg as utilSvg } from './utils/svg.js';

export const svgExport = {

  /**
   * @type {JsonStatReader}
   */
  _reader: null,

  /**
   * The style sheet to load and include in the exported svg.
   */
  urlStylesheet: config.source.css + 'resultmap.css',

  /**
   * Size of the exported map.
   */
  size: 1080,

  /**
   *
   */
  padding: 10,

  /**
   *
   * @param {JsonStatReader} jsonStatReader
   */
  init (jsonStatReader) {
    this._reader = jsonStatReader;
  },

  /**
   * Export the region layer of the map as a svg with legend and table header.
   * Important: SVG font-size in the map app needs to be the same as in the exported standalone,
   *    otherwise the size calculations when exporting are wrong.
   *
   * @param {module:map} map
   * @param {module:gui/legend} legend
   * @param {module:gui/header} header
   * @param {module:gui/copyright} copyright
   * @param {DataSeries} rowData
   * @return {Promise<Blob>}
   */
  async export (map, legend, header, copyright, rowData) {
    const varNr = this._reader.getRegionId();

    // fetch and convert the region
    let g, x, y, w, h, bbox;
    const strSvg = await this._fetchSvg(varNr);
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(strSvg, 'image/svg+xml');
    const svg = xmlDoc.firstElementChild;
    utilSvg.setAttributes(svg, {
      class: 'export temp'
    });
    document.body.before(svg);  // append to doc in order to calculate bounding boxes
    bbox = svg.getBBox({
      stroke: true,
      fill: true
    });
    x = 0;
    y = 0;
    w = Math.ceil(bbox.width);
    h = Math.ceil(bbox.height);

    this._addPropertyRegion(svg, rowData);

    // add symbols or labels at the label points
    if (this._reader.isChoropleth() === false) {
      this._addPropertyCircle(svg, rowData);
    }
    if (document.getElementById('viewSetting-label').checked) {
      this._addLabel(svg, rowData);
    }

    // add the header
    g = header.exportSvg();
    svg.append(g);
    bbox = g.getBBox();
    y = Math.ceil(bbox.height * 0.75);
    utilSvg.translate(svg.getElementById('region'), x, y);
    utilSvg.translate(svg.getElementById('labelPoint'), x, y);
    h += y;

    // add the legend
    g = legend.exportSvg();
    svg.append(g);
    bbox = g.getBBox();
    x = w + this.padding;
    utilSvg.translate(g, x, 0);
    w = x + Math.ceil(bbox.width);

    // add the footer
    g = copyright.exportSvg();
    svg.append(g);
    bbox = g.getBBox();
    x = 0;
    y = h + 2 * this.padding;
    utilSvg.translate(g, x, y);
    h = y + Math.ceil(bbox.height);

    // adjust overall dimensions
    x = 0 - this.padding;
    y = 0 - this.padding;
    w += 2 * this.padding;
    h += 2 * this.padding;
    svg.setAttribute('viewBox', [x, y, w, h].join(' '));
    svg.remove();

    // add the style sheet
    let strStyle = await fetch(this.urlStylesheet).then(fhr.text);
    let style = utilSvg.create('style');
    style.append(strStyle);
    svg.firstElementChild.before(style);
    svg.classList.remove('temp');

    // create the binary file
    const serializer = new XMLSerializer();
    return new Blob([serializer.serializeToString(svg)], {
      type: 'image/svg+xml;charset=utf-8'
    });
  },

  /**
   * Add the color to the SVG (regions).
   * Queries the SVG for the polygon paths and sets the color property according to the row data.
   * @param {SVGSVGElement} svg
   * @param {DataSeries} rowData
   */
  _addPropertyRegion (svg, rowData) {
    // add the colors to the geoJson
    let css = 'export__region' + (this._reader.isChoropleth() ? ' export__region_choropleth' : ' export__region_proportional');
    svg.querySelectorAll('g#region path[data-region-id]').forEach(el => {
      const regionItem = this.findItem(rowData, el);
      utilSvg.setAttributes(el, {
        fill: regionItem ? regionItem.color : 'transparent',
        class: css
      });

    });
  },

  /**
   * Add the circles to the SVG (regions).
   * Queries the SVG for the label points and sets the fill and radius property according to the row data.
   * @param {SVGSVGElement} svg
   * @param {DataSeries} rowData
   * @private
   */
  _addPropertyCircle (svg, rowData) {
    svg.querySelectorAll('g#labelPoint circle').forEach(el => {
      const regionItem = this.findItem(rowData, el);
      if (regionItem) { // some regions might not have data, e.g. RRODREG = 0
        let css = legendProportional.getCssClass(regionItem.value);
        css = css.map(cssClass => {
          return cssClass === 'legend-data-svg__circle' ? 'export__circle' : cssClass;
        });
        utilSvg.setAttributes(el, {
          r: regionItem.radius,
          fill: config.color.circle.posFill,
          class: css.join(' ')
        });
      }
    });
  },

  /**
   * Add the labels to the SVG (regions).
   * Queries the SVG for the label points and inserts the text elements at that location according to the row data.
   * @param {SVGSVGElement} svg
   * @param {DataSeries} rowData
   * @private
   */
  _addLabel (svg, rowData) {
    svg.querySelectorAll('g#labelPoint circle').forEach(el => {
      const regionItem = this.findItem(rowData, el);
      if (regionItem) {
        const label = dataSeries.createStr(regionItem, false);
        const txt = utilSvg.createText(el.getAttribute('cx'), el.getAttribute('cy'), label, {
          stroke: '#fff',
          fill: '#000',
          'stroke-width': '2',
          'text-anchor': 'middle',
          'dominant-baseline': 'middle',
          'paint-order': 'stroke'
        });
        el.after(txt);
      }
    });
  },

  /**
   * Fetch the regional demarcation as SVG from the service.
   * @return {Promise<GeoJSON>}
   * @private
   */
  _fetchSvg (varNr) {
    return fetch(config.service.geo + '/geometry/svg/' + varNr)
      .then(fhr.text);
  },

  /**
   * Find the row data item for the given SVG element.
   * @param {DataSeries} rowData
   * @param {SVGElement} el element with dataset attribute 'data-region-id'
   * @return {DataSeriesItem} item of undefined if not found
   */
  findItem (rowData, el) {
    return rowData.series.find(item => item.id === String(el.dataset.regionId));
  }

};